/* One deck — chart rail left (anchor highlight + live dot updates), card
 * stack right with blank peek edges, settled steps collapsing to slim ✓
 * lines; source links immediately open the standalone NOLOS reader. */

import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchDeck } from './api'
import { openDocumentReader, type DocumentRef, type ReaderContext } from '../../lib/documentReader'
import SourceDocumentList from '../documents/SourceDocumentList'
import '../documents/reader.css'
import RkCard, { type OpenDocPayload } from './RkCard'
import RkClaimsCard from './RkClaimsCard'
import RkRecoCard from './RkRecoCard'
import RkWalkCard from './RkWalkCard'
import BookActionsPanel from './BookActionsPanel'
import { cardBookProposalIds, ordinaryBookCard } from './bookActionsPanel'
import { selectedManualCards, type ManualBookSelection } from './manualBookRouting'
import type { DeckResponse, RailNode } from './types'

/* Canon (2026-08-31 walk finding): the rail dot is the OBJECT TYPE —
 * entity navy, instrument blue, position amber. State never colors a
 * dot (state = mono badges on the card); the old state classes
 * (newo/upd/inf/qq) are retired with the banned dashed-inferred style. */
const ROLE_CLASS: Record<string, string> = {
  holder: 'r-ent',
  issuer: 'r-ent',
  entity: 'r-ent',
  instrument: 'r-ins',
  position: 'r-pos',
  // A rail trade rides its position's arm — amber, the color of what it
  // moves (issuance-side rails are the instrument deck's own drawing).
  trade: 'r-trd',
}

function RailNodeView({ node, anchored }: { node: RailNode; anchored: boolean }) {
  const cls = [
    'rnode',
    ROLE_CLASS[node.role] ?? 'r-ent',
    node.sub ? 'subn' : '',
    anchored ? 'anchored' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <div className={cls} data-rail-key={node.key} data-rail-state={node.state}>
      <div className="role">{node.role}</div>
      <div className="nm">
        {node.role === 'position' ? (node.balance_state ?? 'no balance') : node.name}
      </div>
      {node.note ? <div className="note">{node.note}</div> : null}
    </div>
  )
}

interface Props {
  deckId: string
  manualSelection?: ManualBookSelection
  /** Called when the last card of the deck settles. */
  onDeckSettled?: () => void
  /** D2 (2026-08-31): called after EVERY decision — the stream recomputes
   * downstream cards immediately instead of waiting for the heartbeat. */
  onDecision?: () => void
  onPaneToggle?: (open: boolean) => void
  onBookPendingChange?: (pending: boolean) => void
}

export default function RkDeck({
  deckId,
  manualSelection,
  onDeckSettled,
  onDecision,
  onPaneToggle,
  onBookPendingChange,
}: Props) {
  const [deck, setDeck] = useState<DeckResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  /* Session ledger: cards settled here collapse to slim ✓ lines; the
   * server drops settled rows on refetch, so the slims are ours to keep. */
  const [done, setDone] = useState<{ cardId: string; slim: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const requestId = useRef(0)
  const selectionKey = JSON.stringify([deckId, manualSelection ?? null])

  const load = useCallback(async () => {
    const request = ++requestId.current
    setLoading(true)
    try {
      const fresh = await fetchDeck(deckId)
      if (request !== requestId.current) return
      setDeck(fresh)
      setLoadedKey(selectionKey)
      setError(null)
    } catch (err) {
      if (request === requestId.current) {
        setError(err instanceof Error ? err.message : 'failed to load deck')
        setErrorKey(selectionKey)
      }
    } finally {
      if (request === requestId.current) setLoading(false)
    }
  }, [deckId, selectionKey])

  const invalidateRead = useCallback(() => {
    requestId.current++
  }, [])
  useEffect(() => {
    setDone([])
    setDeck(null)
    void load()
    return invalidateRead
  }, [load, invalidateRead])

  /* The surface's .paneled margin must never outlive this deck: advancing
   * with the panel open left the card compressed against a panel that no
   * longer existed (Ale 2026-08-19). */
  useEffect(() => () => onPaneToggle?.(false), [onPaneToggle])

  const unavailable = (message: string) => (
    <div>
      {manualSelection
        ? done.map((item, index) => (
            <div className="slim" key={`${item.cardId}:${index}`}>
              {item.slim}
            </div>
          ))
        : null}
      <div className="errline">{message}</div>
      {manualSelection ? (
        <button type="button" className="btn" disabled={loading} onClick={() => void load()}>
          Reload review
        </button>
      ) : null}
    </div>
  )
  if (error && errorKey === selectionKey) return unavailable(error)
  if (!deck || loadedKey !== selectionKey) return <div className="sub">loading deck…</div>

  const doneIds = new Set(done.map((d) => d.cardId))
  const manualCards = manualSelection ? selectedManualCards(deck, manualSelection) : undefined
  if (manualSelection && !manualCards)
    return unavailable(
      'This manual Book selection changed or includes other work. Refresh the queue before continuing.'
    )
  const liveCards = manualCards ?? deck.cards.filter((c) => !doneIds.has(c.card_id))
  const active = liveCards[0] ?? null
  const behind = Math.max(0, liveCards.length - 1)

  const anchor = deck.rail.find((node) => node.key === active?.anchor_key)
  const anchorKind =
    anchor?.role === 'holder' || anchor?.role === 'issuer'
      ? 'entity'
      : anchor?.role === 'position'
        ? 'segment'
        : anchor?.role
  const context: ReaderContext = {
    fundId: deck.fund_ids?.length === 1 ? deck.fund_ids[0] : null,
    focus: anchor?.object_id && anchorKind ? `${anchorKind}:${anchor.object_id}` : null,
    returnTo: window.location.pathname + window.location.search,
  }
  const documents: DocumentRef[] = []
  if (deck.document_id) documents.push(deck.document_ref ?? { document_id: deck.document_id })
  for (const row of [...(active?.rows ?? []), ...(active?.objects ?? [])]) {
    if (row.evidence?.document_id)
      documents.push({ document_id: row.evidence.document_id, page: row.evidence.page })
  }
  for (const claim of active?.claims ?? []) {
    for (const chip of claim.chips ?? [])
      if (chip.document_id)
        documents.push(chip.document_ref ?? { document_id: chip.document_id, page: chip.page })
  }
  const openDoc = (p: OpenDocPayload) => {
    if (!p.documentId) return
    openDocumentReader(p.documentId, {
      ...context,
      page: p.page,
      proposalId: p.proposalId,
      returnTo: window.location.pathname + window.location.search,
    })
  }

  const handleDone = (cardId: string) => (slim: string) => {
    setDone((prev) => [...prev, { cardId, slim }])
    // Live dot updates: the rail states are the server's — refetch.
    load()
    // D2: every decision recomputes the stream behind it — the deck
    // refetch above recomputes THIS deck's downstream cards; the
    // decision hook recomputes the queue (stages, gates, siblings).
    onDecision?.()
    if (liveCards.length <= 1) onDeckSettled?.()
  }

  /* v13: a card carrying its own ownership chart replaces the left rail
   * (the chart IS the segment walk — two copies would drift). The walk
   * card draws its segment in-card the same way. */
  const hideRail =
    !!active?.chart ||
    (active?.stage === 'walk' && !!active?.walk) ||
    // An empty rail is a 168 px hole beside the card, not a chain.
    deck.rail.length === 0

  const bookProposalIds = active ? cardBookProposalIds(active) : []
  const useBookActions =
    !manualSelection && !!active && ordinaryBookCard(active, deck.group_id, bookProposalIds)
  const refreshDeck = () => {
    void load()
    onDecision?.()
  }
  const activeCardView = active ? (
    active.stage === 'walk' && active.walk ? (
      <RkWalkCard
        key={active.card_id}
        card={active}
        deck={deck}
        onDone={handleDone(active.card_id)}
        onOpenDoc={openDoc}
      />
    ) : active.stage === 'reconcile' ? (
      <RkRecoCard
        key={active.card_id}
        card={active}
        deck={deck}
        onDone={handleDone(active.card_id)}
        onOpenDoc={openDoc}
      />
    ) : (active.claims?.length ?? 0) > 0 ? (
      <RkClaimsCard
        key={active.card_id}
        card={active}
        deck={deck}
        onDone={handleDone(active.card_id)}
        onRefresh={refreshDeck}
        onPendingChange={manualSelection ? onBookPendingChange : undefined}
        onOpenDoc={openDoc}
      />
    ) : (
      <RkCard
        key={active.card_id}
        allowedProposalIds={manualSelection?.proposalIds}
        card={active}
        deck={deck}
        onDone={handleDone(active.card_id)}
        onRefresh={refreshDeck}
        onPendingChange={manualSelection ? onBookPendingChange : undefined}
        onOpenDoc={openDoc}
      />
    )
  ) : null

  return (
    <div className="rkgrp" data-deck-id={deck.deck_id} data-deck-stage={deck.stage}>
      <div className="deck">
        {hideRail ? null : (
          <div className="crail" data-testid="rail">
            {deck.rail.map((n) => (
              <RailNodeView key={n.key} node={n} anchored={active?.anchor_key === n.key} />
            ))}
          </div>
        )}
        <div className="stack">
          <SourceDocumentList documents={documents} context={context} />
          {manualSelection && deck.source_note ? <p className="sub">{deck.source_note}</p> : null}
          {deck.multi_segment_label ? (
            <div className="xlink">{deck.multi_segment_label}</div>
          ) : null}
          <div className="slims" data-testid="slims">
            {done.map((d, index) => (
              <div key={`${d.cardId}:${index}`} className="slim">
                {d.slim}
              </div>
            ))}
          </div>
          <div className="backs" aria-hidden="true">
            {behind >= 2 ? (
              <>
                <div className="back b2" />
                <div className="back b1" />
              </>
            ) : behind === 1 ? (
              <div className="back b1" />
            ) : null}
          </div>
          <fieldset
            disabled={!!manualSelection && loading}
            style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
            aria-busy={!!manualSelection && loading}
          >
            {active ? (
              useBookActions ? (
                <BookActionsPanel
                  key={active.card_id}
                  groupId={deck.group_id!}
                  card={active}
                  fundIds={deck.fund_ids}
                  onRefresh={refreshDeck}
                  onPendingChange={onBookPendingChange}
                  fallback={activeCardView}
                />
              ) : (
                activeCardView
              )
            ) : (
              <div className="slim">
                {manualSelection
                  ? 'No open rows remain in this selection.'
                  : '✓ all settled on this deck'}
              </div>
            )}
          </fieldset>
        </div>
      </div>
    </div>
  )
}
